// frontend/modules/loan-disbursement/stores/loan-disbursement-store.ts
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AppapproDTO, DisbursementResult } from '@/shared/api/model';
import { LoanDisbursementState, LoanDisbursementActions, LoanDisbursementFilters } from '../types';

interface LoanDisbursementStore extends LoanDisbursementState, LoanDisbursementActions {}

const initialState: LoanDisbursementState = {
  // UI State
  selectedLoan: null,
  isLoading: false,
  isProcessing: false,
  showDisbursementModal: false,
  showDetailsModal: false,
  
  // Receipt Modal State
  isReceiptModalVisible: false,
  disbursementResult: null,
  
  // Error State
  error: null,
  
  // Data
  approvedLoans: [],
  disbursementHistory: [],
  filters: {
    glNo: '',
    customerName: '',
    officer: '',
    fromDate: '',
    toDate: ''
  },
  
  // Pagination
  currentPage: 1,
  pageSize: 10,
  totalRecords: 0,
};

export const useLoanDisbursementStore = create<LoanDisbursementStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // UI Actions
      setSelectedLoan: (loan: AppapproDTO | null) => {
        set({ selectedLoan: loan }, false, 'setSelectedLoan');
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading }, false, 'setLoading');
      },

      setProcessing: (processing: boolean) => {
        set({ isProcessing: processing }, false, 'setProcessing');
      },

      setShowDisbursementModal: (show: boolean) => {
        set({ showDisbursementModal: show }, false, 'setShowDisbursementModal');
      },

      setShowDetailsModal: (show: boolean) => {
        set({ showDetailsModal: show }, false, 'setShowDetailsModal');
      },

      // Receipt Modal Actions
      setIsReceiptModalVisible: (visible: boolean) => {
        set({ isReceiptModalVisible: visible }, false, 'setIsReceiptModalVisible');
      },

      setDisbursementResult: (result: DisbursementResult | null) => {
        set({ disbursementResult: result }, false, 'setDisbursementResult');
      },

      openReceiptModal: (result: DisbursementResult) => {
        set({ 
          isReceiptModalVisible: true, 
          disbursementResult: result 
        }, false, 'openReceiptModal');
      },

      closeReceiptModal: () => {
        set({ 
          isReceiptModalVisible: false, 
          disbursementResult: null 
        }, false, 'closeReceiptModal');
      },

      printReceipt: () => {
        const { disbursementResult } = get();
        if (disbursementResult) {
          // Create print window with receipt content
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            printWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Loan Disbursement Receipt</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    .header { text-align: center; margin-bottom: 30px; }
                    .info-row { display: flex; justify-content: space-between; margin: 10px 0; }
                    .section { margin: 20px 0; }
                    .section h3 { border-bottom: 1px solid #ccc; padding-bottom: 5px; }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <h1>Loan Disbursement Receipt</h1>
                    <p>Transaction #: ${disbursementResult.transactionNumber}</p>
                  </div>
                  <div class="section">
                    <h3>Transaction Details</h3>
                    <div class="info-row">
                      <span>Amount:</span>
                      <span>${disbursementResult.disbursedAmount}</span>
                    </div>
                    <div class="info-row">
                      <span>Status:</span>
                      <span>${disbursementResult.status}</span>
                    </div>
                    <div class="info-row">
                      <span>Branch:</span>
                      <span>${disbursementResult.branch}</span>
                    </div>
                  </div>
                </body>
              </html>
            `);
            printWindow.document.close();
            printWindow.print();
          }
        }
      },

      downloadVoucher: () => {
        const { disbursementResult } = get();
        if (disbursementResult) {
          // Helper function to format currency
          const formatCurrency = (amount: number | undefined | null) => {
            if (amount === undefined || amount === null || isNaN(amount)) {
              return 'TZS 0.00';
            }
            return new Intl.NumberFormat('en-TZ', {
              style: 'currency',
              currency: 'TZS',
              minimumFractionDigits: 2,
            }).format(amount);
          };

          // Helper function to format date
          const formatDate = (dateString: string | undefined | null) => {
            if (!dateString) {
              return 'Not specified';
            }
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) {
                return 'Invalid Date';
              }
              return date.toLocaleDateString('en-TZ', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });
            } catch (error) {
              return 'Invalid Date';
            }
          };

          // Calculate values
          const disbursedAmount = disbursementResult.disbursedAmount || 0;
          const effectiveDisbursedAmount = disbursementResult.effectiveDisbursedAmount || disbursedAmount;
          const processedAt = disbursementResult.processedAt || disbursementResult.processingTimestamp;
          const numberOfInstallments = disbursementResult.schedule?.scheduleDTO?.length || 0;
          const installmentAmount = numberOfInstallments > 0 
            ? disbursementResult.schedule?.scheduleDTO?.[0]?.totalDue || 0
            : 0;

          // Create a user-friendly text format instead of JSON
          const voucherText = `
LOAN DISBURSEMENT VOUCHER
=========================

Transaction Number: ${disbursementResult.transactionNumber || 'N/A'}
Processing Date: ${formatDate(processedAt)}
Status: Successfully Processed

CUSTOMER INFORMATION
--------------------
Customer Name: ${disbursementResult.customerInfo?.customerName || 'N/A'}
Customer Number: ${disbursementResult.customerInfo?.custNo || 'N/A'}
Branch: ${disbursementResult.customerInfo?.branch || disbursementResult.branch || 'N/A'}
${disbursementResult.customerInfo?.phoneNumber ? `Phone: ${disbursementResult.customerInfo.phoneNumber}` : ''}

LOAN DETAILS
------------
Product: ${disbursementResult.productInfo?.productName || 'N/A'}
Loan Amount: ${formatCurrency(disbursedAmount)}
Amount Received by Customer: ${formatCurrency(effectiveDisbursedAmount)}
Interest Rate: ${disbursementResult.productInfo?.interestRate || 'N/A'}%
Repayment Frequency: ${disbursementResult.productInfo?.repaymentFrequency || 'N/A'}

REPAYMENT INFORMATION
--------------------
Number of Installments: ${numberOfInstallments} installments
${installmentAmount > 0 ? `Monthly Installment Amount: ${formatCurrency(installmentAmount)}` : ''}

${disbursementResult.schedule?.scheduleDTO?.slice(0, 5).map((entry, index) => 
  `Installment ${index + 1}: Due ${formatDate(entry.trxDate)} - ${formatCurrency(entry.totalDue)}`
).join('\n') || ''}
${numberOfInstallments > 5 ? `... and ${numberOfInstallments - 5} more installments` : ''}

SOURCE ACCOUNT
--------------
${disbursementResult.sourceAccountInfo ? `
Account: ${disbursementResult.sourceAccountInfo.accountName || 'N/A'}
Account ID: ${disbursementResult.sourceAccountInfo.accountId || 'N/A'}
` : 'No source account information available'}

PROCESSING DETAILS
------------------
Processed By: ${disbursementResult.processedBy || 'N/A'}
Branch: ${disbursementResult.branch || 'N/A'}
${disbursementResult.notes ? `Notes: ${disbursementResult.notes}` : ''}

---
This voucher serves as proof of loan disbursement.
Generated on: ${new Date().toLocaleString('en-TZ')}
          `.trim();

          const blob = new Blob([voucherText], {
            type: 'text/plain',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `loan-disbursement-voucher-${disbursementResult.transactionNumber || 'unknown'}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      },

      // Error Actions
      setError: (error: any) => {
        set({ error: error }, false, 'setError');
      },

      clearError: () => {
        set({ error: null }, false, 'clearError');
      },

      // Data Actions
      setApprovedLoans: (loans: AppapproDTO[]) => {
        set({ approvedLoans: loans }, false, 'setApprovedLoans');
      },

      setDisbursementHistory: (history: DisbursementResult[]) => {
        set({ disbursementHistory: history }, false, 'setDisbursementHistory');
      },

      setFilters: (filters: Partial<LoanDisbursementFilters>) => {
        console.log('store setFilters called with:', filters);
        const currentFilters = get().filters;
        console.log('store setFilters - current filters:', currentFilters);
        const newFilters = { ...currentFilters, ...filters };
        console.log('store setFilters - new filters:', newFilters);
        set({ filters: newFilters }, false, 'setFilters');
      },

      clearFilters: () => {
        set({ 
          filters: {
            glNo: '',
            customerName: '',
            officer: '',
            fromDate: '',
            toDate: ''
          },
          currentPage: 1 // Reset to first page when clearing filters
        }, false, 'clearFilters');
      },

      // Generic Modal Actions
      openModal: () => {
        set({ showDetailsModal: true }, false, 'openModal');
      },

      closeModal: () => {
        set({ showDetailsModal: false }, false, 'closeModal');
      },

      openDisbursementModal: (loan: AppapproDTO) => {
        set({ 
          selectedLoan: loan, 
          showDisbursementModal: true 
        }, false, 'openDisbursementModal');
      },

      closeDisbursementModal: () => {
        set({ 
          showDisbursementModal: false, 
          selectedLoan: null 
        }, false, 'closeDisbursementModal');
      },

      // Pagination Actions
      setCurrentPage: (page: number) => {
        set({ currentPage: page }, false, 'setCurrentPage');
      },

      setPageSize: (size: number) => {
        set({ 
          pageSize: size,
          currentPage: 1 // Reset to first page when changing page size
        }, false, 'setPageSize');
      },

      setTotalRecords: (total: number) => {
        set({ totalRecords: total }, false, 'setTotalRecords');
      },

      // Reset
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'loan-disbursement-store',
    }
  )
);
